import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, ImageIcon, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  fetchScreenshots,
  deleteScreenshot,
  LOC_STR as S,
  type ScreenshotRow,
} from './localizationExtraApi'

/**
 * A22.12 — Контекстные скриншоты. Screenshots attached to i18n keys so a
 * translator sees WHERE a string is used (avoids out-of-context translations).
 * Dormant until `/admin/localization/screenshots` ships → fetcher fail-soft to
 * empty. Placeholder permission USERS_VIEW.
 */
export function LocalizationScreenshots() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<ScreenshotRow>[]>(
    () => [
      { key: 'title', header: S.title, type: 'text' },
      { key: 'screen', header: S.screen, type: 'text' },
      { key: 'key', header: S.key, type: 'text' },
      { key: 'linked_keys', header: S.linkedKeys, type: 'number' },
      { key: 'uploaded_by', header: S.uploadedBy, type: 'text' },
      { key: 'created_at', header: S.createdAt, type: 'date' },
    ],
    [],
  )

  const doRemove = useCallback(
    async (row: ScreenshotRow) => {
      try {
        await deleteScreenshot(row.id)
        toast(S.removed, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<ScreenshotRow>[]>(
    () => [
      {
        id: 'remove',
        label: S.remove,
        icon: <Trash2 size={14} />,
        danger: true,
        onSelect: doRemove,
      },
    ],
    [doRemove],
  )

  const fetcher = useCallback(fetchScreenshots, [])
  return (
    <>
      <PageHeader
        title={S.screenshotsTitle}
        screenId="A22.12"
        actions={
          <Link
            to="/localization"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {S.back}
          </Link>
        }
      />
      <DataTable<ScreenshotRow>
        screenId="A22.12"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <ImageIcon size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
